/**
 * EditorContextMenu - Renders the context menu for editor elements
 */
import React from 'react'
import { createPortal } from 'react-dom'
import { Menu } from 'antd'
import type { MenuProps } from 'antd'

interface EditorContextMenuProps {
  /** Whether the context menu is visible */
  isVisible: boolean
  /** Position of the context menu */
  position: { x: number; y: number } | null
  /** Menu items to display */
  items: MenuProps['items']
  /** Handler for menu item click */
  onClick: (key: string) => void
  /** Handler to close the menu */
  onClose: () => void
}

/**
 * EditorContextMenu Component
 */
const EditorContextMenu: React.FC<EditorContextMenuProps> = ({
  isVisible,
  position,
  items,
  onClick,
  onClose,
}) => {
  // Handle click outside to close
  React.useEffect(() => {
    if (!isVisible) return

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Element
      if (!target.closest('.editor-context-menu')) {
        onClose()
      }
    }

    // Small delay to avoid immediate close from the same click that opened it
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside)
    }, 100)

    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('click', handleClickOutside)
    }
  }, [isVisible, onClose])

  // Handle escape key to close
  React.useEffect(() => {
    if (!isVisible) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isVisible, onClose])

  // Don't render if not visible or no position
  if (!isVisible || !position) return null

  // Calculate menu dimensions and adjust position if needed
  const menuWidth = 220
  const menuHeight = items ? items.length * 36 + 20 : 200

  let x = position.x
  let y = position.y

  // Ensure menu stays within viewport
  if (x + menuWidth > window.innerWidth - 10) {
    x = position.x - menuWidth - 5
  }
  if (y + menuHeight > window.innerHeight - 10) {
    y = position.y - menuHeight - 5
  }

  // Clamp to viewport
  x = Math.max(10, Math.min(x, window.innerWidth - menuWidth - 10))
  y = Math.max(10, Math.min(y, window.innerHeight - menuHeight - 10))

  // Handle menu item click
  const handleClick: MenuProps['onClick'] = (info) => {
    onClick(info.key)
  }

  const menuElement = (
    <div
      className="editor-context-menu"
      style={{
        position: 'fixed',
        top: y,
        left: x,
        zIndex: 1000,
        backgroundColor: 'white',
        border: '1px solid #d9d9d9',
        borderRadius: '6px',
        boxShadow: '0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',
        minWidth: '180px',
        maxWidth: '280px',
      }}
    >
      <Menu
        items={items}
        onClick={handleClick}
        style={{
          border: 'none',
          borderRadius: '6px',
        }}
      />
    </div>
  )

  // Render as portal to ensure it's on top of everything
  return createPortal(menuElement, document.body)
}

export default EditorContextMenu
